import { setContext } from "../api";
import { auth } from "../auth";
import { Brand } from "../components/shared";
import { usPhoneDigits, usPhoneForApi } from "../lib/format";
import { memberApi } from "../memberApi";
import { offlineStore } from "../offlineDb";
import { applyPlatformIdentity, platformIdentity } from "../platformIdentity";
import {
  Building2,
  CheckCheck,
  ChevronRight,
  Download,
  Home,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Trophy,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function Welcome({ onContinue, install }) {
  const [location, setLocation] = useState("");
  const [discovery, setDiscovery] = useState(null);
  const [discoveryStatus, setDiscoveryStatus] = useState({
    loading: false,
    error: "",
  });

  const findLeagues = async (event) => {
    event.preventDefault();
    const searchLocation = location.trim();
    if (!searchLocation) {
      setDiscovery(null);
      setDiscoveryStatus({
        loading: false,
        error: "Enter a ZIP code or city to find nearby leagues.",
      });
      return;
    }

    setDiscovery(null);
    setDiscoveryStatus({ loading: true, error: "" });
    try {
      setDiscovery(await memberApi.discoverLeagues(searchLocation));
      setDiscoveryStatus({ loading: false, error: "" });
    } catch (error) {
      setDiscoveryStatus({
        loading: false,
        error: error.message || "We could not search for leagues right now.",
      });
    }
  };

  return (
    <main className="welcome-screen">
      <div className="welcome-glow" />
      <div className="brand-lockup">
        <span className="target-mark">◎</span>
        <b>OCHE</b>
        <span>LEAGUE</span>
      </div>
      <section className="welcome-copy">
        <span className="kicker">THE SHOW MUST GO ON</span>
        <h1>
          Your darts.
          <br />
          <em>Your night.</em>
        </h1>
        <p>
          Teams, stats, rosters, and match scoring—ready even when the pub Wi-Fi
          is not.
        </p>
      </section>
      <section
        className="league-discovery"
        aria-labelledby="find-leagues-heading"
      >
        <div className="league-discovery-heading">
          <MapPin aria-hidden="true" />
          <div>
            <span className="kicker">PLAY LOCAL</span>
            <h2 id="find-leagues-heading">Find leagues near you</h2>
          </div>
        </div>
        <form onSubmit={findLeagues}>
          <label htmlFor="league-location">ZIP code or city</label>
          <div className="league-discovery-search">
            <input
              id="league-location"
              autoComplete="postal-code"
              placeholder="Enter ZIP code"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
            <button type="submit" disabled={discoveryStatus.loading}>
              {discoveryStatus.loading ? "Searching…" : "Find leagues"}
            </button>
          </div>
        </form>
        {discoveryStatus.error && (
          <p className="league-discovery-error" role="alert">
            {discoveryStatus.error}
          </p>
        )}
        {discovery && (
          <div className="league-discovery-results" aria-live="polite">
            <p className="league-discovery-summary">
              {discovery.results.length
                ? `${discovery.results.length} ${discovery.results.length === 1 ? "league" : "leagues"} near ${discovery.resolvedLocation}`
                : `No published leagues found near ${discovery.resolvedLocation}. Try another ZIP code or check back soon.`}
            </p>
            {discovery.results.map((result) => (
              <article className="league-result" key={result.seasonId}>
                <div>
                  <b>{result.leagueName}</b>
                  <span>{result.organizationName}</span>
                </div>
                <strong>
                  {result.distanceMiles == null
                    ? result.state
                    : `${result.distanceMiles} mi`}
                </strong>
                <p>
                  {result.seasonName} {result.year} · {result.teamCount}{" "}
                  registered {result.teamCount === 1 ? "team" : "teams"}
                </p>
                <small>
                  <MapPin aria-hidden="true" />{" "}
                  {result.nearestPubName || `${result.city}, ${result.state}`}
                </small>
                {result.teamsAcceptingPlayers > 0 && (
                  <div className="league-recruiting-teams">
                    <em>
                      {result.teamsAcceptingPlayers}{" "}
                      {result.teamsAcceptingPlayers === 1
                        ? "team is"
                        : "teams are"}{" "}
                      recruiting
                    </em>
                    {(result.recruitingTeams || []).map((team) => (
                      <a
                        key={team.teamJoinPublicId}
                        href={`/team-join/${team.teamJoinPublicId}`}
                      >
                        <span>
                          <b>{team.teamName}</b>
                          <small>
                            {team.registrationStatus === "Registered"
                              ? "Registered team"
                              : "Building its roster"}
                            {team.homePubName ? ` · ${team.homePubName}` : ""}
                          </small>
                        </span>
                        <ChevronRight />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
            <small className="league-discovery-attribution">
              {discovery.attribution}
            </small>
          </div>
        )}
      </section>
      <div className="welcome-actions">
        <button className="action-primary" onClick={onContinue}>
          Continue to {platformIdentity.productBrandName}{" "}
          <ChevronRight size={20} />
        </button>
        {!install.installed && (
          <button
            className="action-quiet"
            disabled={!install.available}
            onClick={install.install}
          >
            <Download size={17} />{" "}
            {install.available
              ? "Install on this phone"
              : "Install option appears in your browser menu"}
          </button>
        )}
      </div>
    </main>
  );
}

export function SmsOptInDemo() {
  const [phone, setPhone] = useState("");
  const [consented, setConsented] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    applyPlatformIdentity();
    document.title = `SMS opt-in | ${platformIdentity.productBrandName}`;
  }, []);

  const submit = (event) => {
    event.preventDefault();
    setError("");
    if (!consented) {
      setError(
        "Select the optional SMS consent checkbox to demonstrate enrollment, or continue without SMS.",
      );
      return;
    }
    if (usPhoneDigits(phone).length !== 10) {
      setError("Enter a 10-digit U.S. mobile phone number.");
      return;
    }
    setResult("opted-in");
  };

  return (
    <main className="claim-screen sms-opt-in-screen">
      <Brand />
      <section className="claim-card sms-opt-in-card">
        <div className="claim-icon">
          <ShieldCheck />
        </div>
        <span className="kicker">PUBLIC CONSENT DEMONSTRATION</span>
        <h1>League text updates</h1>
        <p>
          This public page demonstrates the optional SMS consent flow used by
          players of {platformIdentity.productBrandName}, a product of{" "}
          {platformIdentity.legalBusinessName}. No login is required for
          compliance review.
        </p>

        {result ? (
          <div className="sms-opt-in-confirmation" role="status">
            <ShieldCheck />
            <div>
              <h2>
                {result === "opted-in"
                  ? "SMS preference confirmed"
                  : "SMS declined"}
              </h2>
              {result === "opted-in" ? (
                <>
                  <p>
                    <b>Confirmation message shown to the player:</b>
                  </p>
                  <blockquote>
                    {platformIdentity.productBrandName}: You are subscribed to
                    recurring league account and match updates. Message
                    frequency varies. Message and data rates may apply. Reply
                    HELP for help or STOP to cancel.
                  </blockquote>
                </>
              ) : (
                <p>
                  The player can continue using the service without receiving
                  SMS messages.
                </p>
              )}
              <button
                type="button"
                className="action-quiet"
                onClick={() => {
                  setResult("");
                  setConsented(false);
                  setPhone("");
                }}
              >
                Review the opt-in again
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label>
              <span>
                Mobile phone number <small>U.S. numbers only</small>
              </span>
              <div className="us-phone-input">
                <span>+1</span>
                <input
                  aria-label="Mobile phone number"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength="10"
                  placeholder="8175551212"
                  value={usPhoneDigits(phone)}
                  onChange={(event) =>
                    setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                />
              </div>
            </label>
            <fieldset className="claim-preferences">
              <legend>Optional SMS consent</legend>
              <p>
                SMS is not required to register, play in a league, or use the
                service.
              </p>
              <label className="claim-consent">
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(event) => setConsented(event.target.checked)}
                />
                <span>
                  <b>
                    I agree to receive recurring SMS messages from{" "}
                    {platformIdentity.productBrandName}
                  </b>
                  <small>
                    Messages include player and team invitations, roster
                    changes, schedules, match reminders, fee notices, and
                    account notifications. Message frequency varies. Message and
                    data rates may apply. Reply HELP for help, email{" "}
                    {platformIdentity.productSupportEmail}, or reply STOP to
                    cancel. Consent is optional and is not a condition of
                    registration, purchase, or league participation.
                  </small>
                </span>
              </label>
              <p className="claim-legal">
                By opting in, you agree to the{" "}
                <a
                  href="https://api.ocheleague.com/legal/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and acknowledge the{" "}
                <a
                  href="https://api.ocheleague.com/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                . SMS consent is not shared with third parties or affiliates for
                marketing or promotional purposes.
              </p>
            </fieldset>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button className="action-primary" type="submit">
              <span>Confirm SMS opt-in</span>
              <ChevronRight />
            </button>
            <button
              className="action-quiet sms-decline"
              type="button"
              onClick={() => {
                setError("");
                setResult("declined");
              }}
            >
              Continue without SMS
            </button>
          </form>
        )}

        <aside className="sms-review-details">
          <b>Messaging program details</b>
          <dl>
            <div>
              <dt>Brand</dt>
              <dd>{platformIdentity.productBrandName}</dd>
            </div>
            <div>
              <dt>Legal business</dt>
              <dd>{platformIdentity.legalBusinessName}</dd>
            </div>
            <div>
              <dt>Campaign use case</dt>
              <dd>Low Volume Mixed</dd>
            </div>
            <div>
              <dt>Support</dt>
              <dd>
                <a href={`mailto:${platformIdentity.productSupportEmail}`}>
                  {platformIdentity.productSupportEmail}
                </a>
              </dd>
            </div>
          </dl>
        </aside>
      </section>
    </main>
  );
}

export function ClaimScreen({ token, account, onClaimed }) {
  const [preview, setPreview] = useState(null);
  const autoClaimStarted = useRef("");
  const [autoClaimAttempt, setAutoClaimAttempt] = useState(0);
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    emailOptIn: false,
    smsOptIn: false,
    preferredCommunicationChannel: "None",
  });
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
  });
  const identityEmail =
    account?.username || account?.idTokenClaims?.email || "";

  useEffect(() => {
    if (!auth.configured) {
      const demo = {
        organizationName: "Oche League Demo",
        firstName: "Darrn",
        lastName: "Player",
        deliveryTarget: "demo@ocheleague.com",
        status: "Pending",
        expiresAt: new Date(Date.now() + 86400000).toISOString(),
      };
      setPreview(demo);
      setProfile({
        firstName: "Darrin",
        lastName: "Player",
        phone: "",
        emailOptIn: false,
        smsOptIn: false,
        preferredCommunicationChannel: "None",
      });
      setStatus({ loading: false, saving: false, error: "" });
      return;
    }
    memberApi
      .previewClaim(token)
      .then((value) => {
        setPreview(value);
        setProfile({
          firstName: value.firstName,
          lastName: value.lastName,
          phone: "",
          emailOptIn: false,
          smsOptIn: false,
          preferredCommunicationChannel: "None",
        });
        setStatus({ loading: false, saving: false, error: "" });
      })
      .catch((error) =>
        setStatus({ loading: false, saving: false, error: error.message }),
      );
  }, [token, account]);

  const expired =
    preview &&
    (preview.status !== "Pending" ||
      new Date(preview.expiresAt) <= new Date());
  const emailMismatch =
    preview?.deliveryChannel?.toLowerCase() === "email" &&
    preview.deliveryTarget &&
    identityEmail.trim().toLowerCase() !==
      preview.deliveryTarget.trim().toLowerCase();
  const autoClaimEligible =
    auth.configured &&
    preview &&
    account &&
    !expired &&
    !emailMismatch &&
    preview.deliveryChannel?.toLowerCase() === "email";

  useEffect(() => {
    if (!autoClaimEligible) return;
    const attemptKey = `${token}:${identityEmail.toLowerCase()}:${autoClaimAttempt}`;
    if (autoClaimStarted.current === attemptKey) return;
    autoClaimStarted.current = attemptKey;
    setStatus({ loading: false, saving: true, error: "" });
    memberApi
      .confirmClaim(token, {})
      .then(() => memberApi.account())
      .then(onClaimed)
      .catch((error) =>
        setStatus({ loading: false, saving: false, error: error.message }),
      );
  }, [
    account,
    autoClaimAttempt,
    autoClaimEligible,
    identityEmail,
    onClaimed,
    token,
  ]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>CHECKING INVITATION</b>
      </div>
    );
  if (!preview)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Invitation unavailable</h1>
          <p>{status.error || "Ask your captain to send a new invitation."}</p>
          {account && (
            <div className="membership-actions">
              <button
                onClick={() => {
                  history.replaceState({}, "", "/");
                  location.reload();
                }}
              >
                <UserRound /> Player account
              </button>
              <button
                onClick={async () => {
                  await offlineStore.clearForSignOut();
                  await auth.signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </div>
          )}
        </section>
      </main>
    );
  if (!account)
    return (
      <main className="claim-screen">
        <Brand />
        <section className={`claim-card${expired ? " error-card" : ""}`}>
          <div className="claim-icon">
            <Mail />
          </div>
          <span className="kicker">PLAYER INVITATION</span>
          <h1>Join {preview.organizationName}.</h1>
          <p>
            <b>{preview.firstName} {preview.lastName}</b>, create your free {" "}
            {platformIdentity.productBrandName} account to see your team,
            schedule, matches, and statistics.
          </p>
          {preview.deliveryTarget && (
            <p className="imported-name">
              Your invitation was sent to <b>{preview.deliveryTarget}</b>. We’ll
              send a one-time code to verify it.
            </p>
          )}
          {!expired && (
            <>
              <button
                className="action-primary"
                onClick={() => auth.signUp(`/claim/${token}`)}
              >
                <span>Create my Oche account</span>
                <ChevronRight size={19} />
              </button>
              <button
                className="action-quiet"
                onClick={() =>
                  auth.signIn(`/claim/${token}`, preview.deliveryTarget || "")
                }
              >
                <LogIn size={18} />
                I already have an Oche account
              </button>
            </>
          )}
          {expired && (
            <p className="form-error">
              This invitation has expired or was already used. Ask your captain
              to send a new invitation.
            </p>
          )}
        </section>
      </main>
    );
  if (emailMismatch)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <div className="claim-icon">
            <UserRound />
          </div>
          <span className="kicker">WRONG SIGN-IN</span>
          <h1>Use the invited account</h1>
          <p>
            This invitation was sent to <b>{preview.deliveryTarget}</b>, but you
            are signed in as <b>{identityEmail}</b>.
          </p>
          <button
            className="action-primary"
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            <span>Sign out and use {preview.deliveryTarget}</span>
            <LogOut />
          </button>
        </section>
      </main>
    );

  if (autoClaimEligible)
    return (
      <main className="claim-screen">
        <Brand />
        <section className={`claim-card${status.error ? " error-card" : ""}`}>
          <div className="claim-icon">
            {status.error ? <UserRound /> : <ShieldCheck />}
          </div>
          <span className="kicker">
            {status.error ? "CONNECTION INTERRUPTED" : "INVITATION VERIFIED"}
          </span>
          <h1>
            {status.error
              ? "We couldn’t finish connecting your account."
              : `Connecting you to ${preview.organizationName}…`}
          </h1>
          <p>
            {status.error
              ? status.error
              : "Your verified email matches the invitation. No additional confirmation is needed."}
          </p>
          {status.error && (
            <button
              className="action-primary"
              onClick={() => setAutoClaimAttempt((attempt) => attempt + 1)}
            >
              <span>Try again</span>
              <RefreshCw />
            </button>
          )}
        </section>
      </main>
    );

  return (
    <main className="claim-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Building2 />
          <span>
            <small>INVITED TO</small>
            <b>{preview.organizationName}</b>
          </span>
        </div>
        <span className="kicker">CONFIRM YOUR PLAYER</span>
        <h1>Is this you?</h1>
        <p className="imported-name">
          Your roster currently shows{" "}
          <b>
            {preview.firstName} {preview.lastName}
          </b>
          . Correct it below if it was entered differently.
        </p>
        <div className="signed-in-identity">
          <UserRound />
          <span>
            <small>SIGNED IN AS</small>
            <b>{identityEmail}</b>
          </span>
          <button
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            Not you?
          </button>
        </div>
        <label>
          <span>Email address</span>
          <input
            type="email"
            value={identityEmail}
            readOnly
            aria-readonly="true"
          />
        </label>
        <label>
          <span>First name</span>
          <input
            autoComplete="given-name"
            value={profile.firstName}
            onChange={(event) =>
              setProfile({ ...profile, firstName: event.target.value })
            }
          />
        </label>
        <label>
          <span>Last name</span>
          <input
            autoComplete="family-name"
            value={profile.lastName}
            onChange={(event) =>
              setProfile({ ...profile, lastName: event.target.value })
            }
          />
        </label>
        <label>
          <span>
            Mobile phone <small>optional</small>
          </span>
          <div className="us-phone-input">
            <span>+1</span>
            <input
              autoComplete="tel-national"
              inputMode="numeric"
              maxLength="10"
              placeholder="8175551212"
              value={usPhoneDigits(profile.phone)}
              onChange={(event) =>
                setProfile({
                  ...profile,
                  phone: event.target.value.replace(/\D/g, "").slice(0, 10),
                })
              }
            />
          </div>
        </label>
        <fieldset className="claim-preferences">
          <legend>Communication choices</legend>
          <p>
            Email is optional and is used when an important invitation cannot
            reach your in-app notification inbox.
          </p>
          <label className="claim-consent">
            <input
              type="checkbox"
              checked={profile.emailOptIn}
              onChange={(event) =>
                setProfile({
                  ...profile,
                  emailOptIn: event.target.checked,
                  smsOptIn: false,
                  preferredCommunicationChannel: event.target.checked
                    ? "Email"
                    : "None",
                })
              }
            />
            <span>
              <b>Email updates</b>
              <small>
                Receive league invitations, roster, schedule, fee, and
                match-related email.
              </small>
            </span>
          </label>
          <p className="claim-legal">
            After registration, open Updates to optionally turn on browser
            alerts. Review the{" "}
            <a
              href="https://api.ocheleague.com/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://api.ocheleague.com/legal/terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
            .
          </p>
        </fieldset>
        <div className="claim-proof">
          <ShieldCheck />
          <span>
            The invitation—not a name match—is the proof connecting this sign-in
            to the roster record.
          </span>
        </div>
        <button
          className="action-primary"
          disabled={
            expired ||
            !profile.firstName.trim() ||
            !profile.lastName.trim() ||
            status.saving
          }
          onClick={async () => {
            if (!auth.configured) {
              await offlineStore.setPreference(
                "organizationName",
                preview.organizationName,
              );
              onClaimed({ organizationName: preview.organizationName });
              return;
            }
            setStatus({ ...status, saving: true, error: "" });
            try {
              await memberApi.confirmClaim(token, {
                ...profile,
                phone: usPhoneForApi(profile.phone),
              });
              onClaimed(await memberApi.account());
            } catch (error) {
              setStatus({
                loading: false,
                saving: false,
                error: error.message,
              });
            }
          }}
        >
          <span>
            {status.saving
              ? "Connecting your player…"
              : "Claim my player record"}
          </span>
          <ChevronRight />
        </button>
        {expired && (
          <p className="form-error">
            This invitation has expired or was already used.
          </p>
        )}
        {status.error && <p className="form-error">{status.error}</p>}
      </section>
    </main>
  );
}

export function TeamInvitationScreen({ token, account }) {
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
    complete: null,
  });
  const [claimVersion, setClaimVersion] = useState(0);

  useEffect(() => {
    setStatus((value) => ({ ...value, loading: true, error: "" }));
    if (!auth.configured) {
      setPreview({
        leagueName: "Oche League Demo",
        seasonName: "Demo Season",
        seasonYear: new Date().getFullYear(),
        teamName: "Demo Team",
        playerFirstName: "Demo",
        playerLastName: "Player",
        requiresPlayerClaim: false,
        status: "Pending",
        expiresAt: new Date(Date.now() + 86400000).toISOString(),
      });
      setStatus({ loading: false, saving: false, error: "", complete: null });
      return;
    }
    memberApi
      .previewTeamInvitation(token)
      .then((value) => {
        setPreview(value);
        setStatus({ loading: false, saving: false, error: "", complete: null });
      })
      .catch((error) =>
        setStatus({
          loading: false,
          saving: false,
          error: error.message,
          complete: null,
        }),
      );
  }, [token, account, claimVersion]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>CHECKING TEAM INVITATION</b>
      </div>
    );
  if (!preview)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Invitation unavailable</h1>
          <p>{status.error || "Ask your captain to send a new invitation."}</p>
          {account && (
            <div className="membership-actions">
              <button
                onClick={() => {
                  history.replaceState({}, "", "/");
                  location.reload();
                }}
              >
                <UserRound /> Player account
              </button>
              <button
                onClick={async () => {
                  await offlineStore.clearForSignOut();
                  await auth.signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </div>
          )}
        </section>
      </main>
    );
  const unavailable =
    preview.status !== "Pending" || new Date(preview.expiresAt) <= new Date();
  if (!account)
    return (
      <main className="claim-screen">
        <Brand />
        <section className={`claim-card${unavailable ? " error-card" : ""}`}>
          <div className="claim-icon">
            <Users />
          </div>
          <span className="kicker">TEAM INVITATION</span>
          <h1>Join {preview.teamName}.</h1>
          <p>
            <b>{preview.playerFirstName} {preview.playerLastName}</b>, your captain
            saved you a spot in {preview.leagueName}. Create your free account
            to connect your player record and respond.
          </p>
          {!unavailable && (
            <>
              <button
                className="action-primary"
                onClick={() => auth.signUp(`/team-invite/${token}`)}
              >
                <span>Create my Oche account</span>
                <ChevronRight size={19} />
              </button>
              <button
                className="action-quiet"
                onClick={() => auth.signIn(`/team-invite/${token}`)}
              >
                <LogIn size={18} />
                I already have an Oche account
              </button>
            </>
          )}
          {unavailable && (
            <p className="form-error">
              This invitation is no longer active. Ask your captain to send a
              new invitation.
            </p>
          )}
        </section>
      </main>
    );
  if (preview.requiresPlayerClaim)
    return (
      <ClaimScreen
        token={token}
        account={account}
        onClaimed={() => setClaimVersion((value) => value + 1)}
      />
    );
  if (status.complete)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card">
          <div className="claim-icon">
            <ShieldCheck />
          </div>
          <span className="kicker">{status.complete.status}</span>
          <h1>
            {status.complete.isRostered
              ? `Welcome to ${preview.teamName}.`
              : "Invitation declined."}
          </h1>
          <p>{status.complete.message}</p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            <span>Open my dashboard</span>
            <ChevronRight />
          </button>
        </section>
      </main>
    );
  const respond = async (accept) => {
    setStatus((value) => ({ ...value, saving: true, error: "" }));
    try {
      const result = accept
        ? await memberApi.acceptTeamInvitation(token)
        : await memberApi.rejectTeamInvitation(token);
      setStatus({ loading: false, saving: false, error: "", complete: result });
    } catch (error) {
      setStatus((value) => ({ ...value, saving: false, error: error.message }));
    }
  };
  return (
    <main className="claim-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Users />
          <span>
            <small>TEAM INVITATION</small>
            <b>{preview.leagueName}</b>
          </span>
        </div>
        <span className="kicker">
          {preview.seasonName} {preview.seasonYear}
        </span>
        <h1>Join {preview.teamName}?</h1>
        <p>
          <b>
            {preview.playerFirstName} {preview.playerLastName}
          </b>
          , accept to take this roster spot. A player may accept only one team
          in the same league season.
        </p>
        <div className="signed-in-identity">
          <UserRound />
          <span>
            <small>SIGNED IN AS</small>
            <b>{account?.username || account?.idTokenClaims?.email}</b>
          </span>
          <button
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            Not you?
          </button>
        </div>
        {!unavailable && (
          <>
            <button
              className="action-primary"
              disabled={status.saving}
              onClick={() => respond(true)}
            >
              <span>
                {status.saving ? "Saving…" : "Accept team invitation"}
              </span>
              <ChevronRight />
            </button>
            <button
              className="action-quiet"
              disabled={status.saving}
              onClick={() => respond(false)}
            >
              Decline — remain available for another team
            </button>
          </>
        )}
        {unavailable && (
          <div className="invitation-unavailable">
            <b>
              {preview.status === "Accepted"
                ? "This roster spot was already accepted."
                : preview.status === "Rejected"
                  ? "This invitation was declined."
                  : preview.status === "Expired"
                    ? "This invitation has expired."
                    : "This invitation is no longer active."}
            </b>
            <span>
              You are not trapped here. Open your player account or sign out to
              use another account.
            </span>
            <div className="membership-actions">
              <button
                onClick={() => {
                  history.replaceState({}, "", "/");
                  location.reload();
                }}
              >
                <UserRound /> Player account
              </button>
              <button
                onClick={async () => {
                  await offlineStore.clearForSignOut();
                  await auth.signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </div>
          </div>
        )}
        {status.error && <p className="form-error">{status.error}</p>}
      </section>
    </main>
  );
}

export function TeamJoinScreen({ publicId, account }) {
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
    complete: null,
  });

  useEffect(() => {
    memberApi
      .previewTeamJoin(publicId)
      .then((value) => {
        setPreview(value);
        setStatus({ loading: false, saving: false, error: "", complete: null });
      })
      .catch((error) =>
        setStatus({
          loading: false,
          saving: false,
          error: error.message,
          complete: null,
        }),
      );
  }, [publicId]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>CHECKING TEAM</b>
      </div>
    );
  if (!preview)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Team unavailable</h1>
          <p>
            {status.error || "This recruiting link is no longer available."}
          </p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            <span>Find another league</span>
            <ChevronRight />
          </button>
        </section>
      </main>
    );
  if (status.complete)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card">
          <div className="claim-icon">
            <CheckCheck />
          </div>
          <span className="kicker">REQUEST SENT</span>
          <h1>{preview.teamName} received your request.</h1>
          <p>{status.complete.message}</p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            <span>Open my dashboard</span>
            <ChevronRight />
          </button>
        </section>
      </main>
    );
  const unavailable = !preview.isSeasonActive || !preview.isAcceptingPlayers;
  const requestToJoin = async () => {
    setStatus((value) => ({ ...value, saving: true, error: "" }));
    try {
      await setContext({
        organizationId: preview.organizationId,
        organizationName: preview.organizationName,
      });
      const result = await memberApi.requestTeamJoin(publicId);
      setStatus({ loading: false, saving: false, error: "", complete: result });
    } catch (error) {
      setStatus((value) => ({ ...value, saving: false, error: error.message }));
    }
  };
  return (
    <main className="claim-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Users />
          <span>
            <small>{preview.leagueName}</small>
            <b>{preview.organizationName}</b>
          </span>
        </div>
        <span className="kicker">
          {preview.seasonName} {preview.seasonYear}
        </span>
        <h1>Request to join {preview.teamName}</h1>
        <p>
          This team is building its roster for {preview.nightName}. The captain
          will review your request before you are added.
        </p>
        {!account && !unavailable && (
          <button
            className="action-primary"
            onClick={() => auth.signIn(`/team-join/${publicId}`)}
          >
            <span>Sign in to request a spot</span>
            <LogIn />
          </button>
        )}
        {account && !unavailable && (
          <button
            className="action-primary"
            disabled={status.saving}
            onClick={requestToJoin}
          >
            <span>
              {status.saving ? "Sending request…" : "Ask to join this team"}
            </span>
            <ChevronRight />
          </button>
        )}
        {unavailable && (
          <div className="invitation-unavailable">
            <b>This team is not currently accepting player requests.</b>
            <span>
              Return to league discovery to find another recruiting team.
            </span>
          </div>
        )}
        {status.error && <p className="form-error">{status.error}</p>}
      </section>
    </main>
  );
}

export function OrganizationPicker({ organizations, onSelect }) {
  return (
    <main className="organization-screen">
      <Brand />
      <section>
        <span className="kicker">YOUR DARTS COMMUNITY</span>
        <h1>Choose an organization</h1>
        <p>
          Your player identity travels with you. Each organization keeps its own
          teams, fees, and membership.
        </p>
        <div className="organization-list">
          {organizations.map((organization) => (
            <button
              key={organization.organizationId}
              onClick={() => onSelect(organization)}
            >
              <Building2 />
              <span>
                <b>{organization.organizationName}</b>
                <small>{organization.role}</small>
              </span>
              <ChevronRight />
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

export function TeamRegistrationScreen({ publicId, account }) {
  const newTeamId = useRef(crypto.randomUUID());
  const [season, setSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [choice, setChoice] = useState("new");
  const [form, setForm] = useState({
    teamName: "",
    nightId: "",
    homePubId: "",
    captainFirstName: "",
    captainLastName: "",
    captainPhone: "",
  });
  const [captainProfile, setCaptainProfile] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
    complete: null,
  });

  useEffect(() => {
    (async () => {
      try {
        const details = await memberApi.teamRegistration(publicId);
        setSeason(details);
        if (details.nights.length === 1)
          setForm((value) => ({ ...value, nightId: details.nights[0].id }));
        if (account) {
          const profile =
            await memberApi.captainProfileForRegistration(publicId);
          setCaptainProfile(profile);
          setForm((value) => ({
            ...value,
            captainFirstName: profile.firstName,
            captainLastName: profile.lastName,
            captainPhone: usPhoneDigits(profile.phoneNumber),
          }));
          setTeams(
            (await memberApi.captainTeamsForRegistration(publicId)).filter(
              (team) => !team.alreadyRegistered,
            ),
          );
        }
        setStatus((value) => ({ ...value, loading: false }));
      } catch (error) {
        setStatus({
          loading: false,
          saving: false,
          error: error.message,
          complete: null,
        });
      }
    })();
  }, [publicId, account]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>OPENING REGISTRATION</b>
      </div>
    );
  if (!season)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Registration link unavailable</h1>
          <p>
            {status.error || "Ask the league organizer for a current link."}
          </p>
        </section>
      </main>
    );
  if (status.complete)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card registration-complete">
          <div className="claim-icon">
            <ShieldCheck />
          </div>
          <span className="kicker">TEAM CREATED</span>
          <h1>{status.complete.teamName} is ready.</h1>
          <p>
            You are the team captain. Build your roster, invite players, and
            submit the team when at least six players are ready.
          </p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            Manage my team <ChevronRight />
          </button>
        </section>
      </main>
    );

  return (
    <main className="claim-screen team-registration-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Trophy />
          <span>
            <small>REGISTER FOR</small>
            <b>{season.organizationName}</b>
          </span>
        </div>
        <span className="kicker">
          {season.leagueName} · {season.seasonName} {season.year}
        </span>
        <h1>Bring your team.</h1>
        <p>
          {season.registrationAvailable
            ? "Sign in, choose a playing night, and become the captain of your team."
            : "Team registration is not currently open for this season."}
        </p>
        {!account ? (
          <button
            className="action-primary"
            disabled={!season.registrationAvailable}
            onClick={() => auth.signIn(`/register-team/${publicId}`)}
          >
            <span>Sign up or log in</span>
            <LogIn />
          </button>
        ) : (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              const submitted = new FormData(event.currentTarget);
              const captainFirstName = String(
                submitted.get("captainFirstName") || form.captainFirstName,
              ).trim();
              const captainLastName = String(
                submitted.get("captainLastName") || form.captainLastName,
              ).trim();
              const captainPhone = usPhoneForApi(
                submitted.get("captainPhone") || form.captainPhone,
              );
              setForm((value) => ({
                ...value,
                captainFirstName,
                captainLastName,
                captainPhone: usPhoneDigits(captainPhone),
              }));
              setStatus((value) => ({ ...value, saving: true, error: "" }));
              try {
                const result = await memberApi.registerCaptainTeam(publicId, {
                  teamId: choice === "new" ? null : choice,
                  newTeamId: choice === "new" ? newTeamId.current : null,
                  teamName: choice === "new" ? form.teamName : "",
                  nightId: form.nightId,
                  homePubId: form.homePubId || null,
                  captainFirstName,
                  captainLastName,
                  captainPhone,
                });
                await setContext({
                  organizationId: season.organizationId,
                  organizationName: season.organizationName,
                });
                setStatus({
                  loading: false,
                  saving: false,
                  error: "",
                  complete: result,
                });
              } catch (error) {
                setStatus((value) => ({
                  ...value,
                  saving: false,
                  error: error.message,
                }));
              }
            }}
          >
            {captainProfile && !captainProfile.isComplete && (
              <fieldset className="captain-profile-fields">
                <legend>Your captain profile</legend>
                <p>
                  Entra only supplied your sign-in email. Tell Oche who will own
                  this team.
                </p>
                <label>
                  <span>Email address</span>
                  <input type="email" value={captainProfile.email} readOnly />
                </label>
                <label>
                  <span>First name</span>
                  <input
                    name="captainFirstName"
                    required
                    maxLength="100"
                    autoComplete="given-name"
                    value={form.captainFirstName}
                    onChange={(event) =>
                      setForm({ ...form, captainFirstName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input
                    name="captainLastName"
                    required
                    maxLength="100"
                    autoComplete="family-name"
                    value={form.captainLastName}
                    onChange={(event) =>
                      setForm({ ...form, captainLastName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>
                    Phone <small>optional</small>
                  </span>
                  <div className="us-phone-input">
                    <span>+1</span>
                    <input
                      name="captainPhone"
                      maxLength="10"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      placeholder="8175551212"
                      value={usPhoneDigits(form.captainPhone)}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          captainPhone: event.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10),
                        })
                      }
                    />
                  </div>
                </label>
              </fieldset>
            )}
            {teams.length > 0 && (
              <label>
                <span>Team</span>
                <select
                  value={choice}
                  onChange={(event) => setChoice(event.target.value)}
                >
                  <option value="new">Create a new team</option>
                  {teams.map((team) => (
                    <option value={team.id} key={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {choice === "new" && (
              <label>
                <span>Team name</span>
                <input
                  required
                  maxLength="200"
                  value={form.teamName}
                  onChange={(event) =>
                    setForm({ ...form, teamName: event.target.value })
                  }
                  placeholder="The Board Members"
                />
              </label>
            )}
            <label>
              <span>Division / night</span>
              <select
                required
                value={form.nightId}
                onChange={(event) =>
                  setForm({ ...form, nightId: event.target.value })
                }
              >
                <option value="">Choose a playing night</option>
                {season.nights.map((night) => (
                  <option key={night.id} value={night.id}>
                    {night.division} · {night.name} · {night.weekday}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>
                Home pub <small>optional</small>
              </span>
              <select
                value={form.homePubId}
                onChange={(event) =>
                  setForm({ ...form, homePubId: event.target.value })
                }
              >
                <option value="">Choose later</option>
                {season.pubs.map((pub) => (
                  <option key={pub.id} value={pub.id}>
                    {pub.name}
                    {pub.city ? ` · ${pub.city}` : ""}
                  </option>
                ))}
              </select>
            </label>
            {status.error && <p className="form-error">{status.error}</p>}
            <button
              className="action-primary"
              disabled={
                !season.registrationAvailable ||
                !form.nightId ||
                (captainProfile &&
                  !captainProfile.isComplete &&
                  (!form.captainFirstName.trim() ||
                    !form.captainLastName.trim())) ||
                status.saving
              }
            >
              <span>
                {status.saving
                  ? "Creating your team…"
                  : choice === "new"
                    ? "Create my team"
                    : "Register selected team"}
              </span>
              <ChevronRight />
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
